import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Microphone, Stop, SpeakerHigh, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import useLang from "../hooks/useLang";

/**
 * Records audio → sends to backend for Whisper transcript → voice-chat → plays back TTS audio.
 */
export default function VoiceChat({ clone, disabled, onExchange }) {
  const { t } = useLang();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  const consentKey = `voice_consent_${clone.id}`;
  const [consented, setConsented] = useState(() => localStorage.getItem(consentKey) === "1");
  const [saveConversations, setSaveConversations] = useState(() => localStorage.getItem(`voice_save_${clone.id}`) !== "0");
  const [showConsent, setShowConsent] = useState(false);
  const [state, setState] = useState("idle"); // idle | recording | transcribing | thinking | speaking
  const [lastAudioUrl, setLastAudioUrl] = useState(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (lastAudioUrl) URL.revokeObjectURL(lastAudioUrl);
    };
  // eslint-disable-next-line
  }, []);

  const enable = () => {
    localStorage.setItem(consentKey, "1");
    localStorage.setItem(`voice_save_${clone.id}`, saveConversations ? "1" : "0");
    setConsented(true);
    setShowConsent(false);
  };

  const toggleSave = (v) => {
    setSaveConversations(v);
    localStorage.setItem(`voice_save_${clone.id}`, v ? "1" : "0");
  };

  const start = async () => {
    if (!consented) {
      setShowConsent(true);
      return;
    }
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error(t("voiceUnavailable"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        await processAudio(blob);
      };
      mr.start();
      setState("recording");
    } catch (err) {
      toast.error(t("micDenied"));
      setState("idle");
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current && state === "recording") {
      mediaRecorderRef.current.stop();
      setState("transcribing");
    }
  };

  const processAudio = async (blob) => {
    try {
      // 1. Transcribe
      const fd = new FormData();
      fd.append("file", blob, "audio.webm");
      fd.append("language", clone.language || "it");
      const { data: tr } = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const transcript = (tr.transcript || "").trim();
      if (!transcript) {
        toast.error("Trascrizione vuota");
        setState("idle");
        return;
      }
      setState("thinking");
      // 2. Voice chat
      const { data } = await api.post(`/clones/${clone.id}/voice-chat`, {
        transcript,
        save: saveConversations,
      }, { timeout: 90000 });
      onExchange?.({ transcript, response: data.response });
      // 3. Play audio
      if (data.audio_base64) {
        setState("speaking");
        const bytes = Uint8Array.from(atob(data.audio_base64), (c) => c.charCodeAt(0));
        const audioBlob = new Blob([bytes], { type: "audio/mpeg" });
        const url = URL.createObjectURL(audioBlob);
        if (lastAudioUrl) URL.revokeObjectURL(lastAudioUrl);
        setLastAudioUrl(url);
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.onended = () => setState("idle");
          audioRef.current.play().catch(() => setState("idle"));
        } else {
          setState("idle");
        }
      } else {
        setState("idle");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
      setState("idle");
    }
  };

  const replay = () => {
    if (audioRef.current && lastAudioUrl) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      setState("speaking");
    }
  };

  const busy = state !== "idle";

  return (
    <div data-testid="voice-chat-container">
      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent className="rounded-md border-zinc-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">{t("voiceConsent")}</DialogTitle>
            <DialogDescription className="text-zinc-600 leading-relaxed pt-3">
              {t("voiceConsentBody")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between border border-zinc-200 rounded-md p-4 my-2">
            <Label className="text-sm">{t("saveConversations")}</Label>
            <Switch checked={saveConversations} onCheckedChange={toggleSave} data-testid="voice-save-switch" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConsent(false)}>{t("cancel")}</Button>
            <Button onClick={enable} data-testid="voice-consent-accept" className="bg-zinc-950 hover:bg-zinc-800 rounded-md">
              {t("voiceEnable")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <audio ref={audioRef} className="hidden" />

      <div className="border border-zinc-200 rounded-md p-4 flex items-center gap-3">
        {state === "recording" ? (
          <Button onClick={stop} disabled={disabled} data-testid="voice-stop-btn" className="bg-red-600 hover:bg-red-700 rounded-full h-14 w-14 p-0 flex items-center justify-center animate-pulse">
            <Stop size={22} weight="fill" />
          </Button>
        ) : (
          <Button onClick={start} disabled={disabled || busy} data-testid="voice-record-btn" className="bg-zinc-950 hover:bg-zinc-800 rounded-full h-14 w-14 p-0 flex items-center justify-center">
            <Microphone size={22} weight="fill" />
          </Button>
        )}
        <div className="flex-1">
          <div className="font-medium text-sm">
            {state === "recording" && <><span className="pulse-dot inline-block w-2 h-2 bg-red-500 rounded-full mr-2 align-middle" />{t("recording")}</>}
            {state === "transcribing" && t("transcribing")}
            {state === "thinking" && t("thinking")}
            {state === "speaking" && <><SpeakerHigh size={14} weight="fill" className="inline mr-1.5" />{t("speaking")}</>}
            {state === "idle" && t("startRecording")}
          </div>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            {consented
              ? (saveConversations ? "Save training: ON" : "Save training: OFF · Anonimo")
              : "Consenso non ancora attivato"}
          </p>
        </div>
        {lastAudioUrl && state === "idle" && (
          <Button onClick={replay} variant="ghost" size="icon" data-testid="voice-replay-btn" className="h-10 w-10">
            <ArrowsClockwise size={16} />
          </Button>
        )}
      </div>
      {consented && (
        <div className="flex items-center justify-between mt-2 px-2">
          <Label className="text-xs text-zinc-500">{t("saveConversations")}</Label>
          <Switch checked={saveConversations} onCheckedChange={toggleSave} data-testid="voice-save-toggle" />
        </div>
      )}
    </div>
  );
}
